import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { WORLDS, type WorldSlug } from '@/lib/worlds';
import { getLibraryCover } from '@/lib/worldAssets';
import { STRINGS } from '@/strings/strings';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorldSelectionSheet({ open, onOpenChange }: Props) {
  const navigate = useNavigate();

  const handleSelect = (slug: WorldSlug) => {
    navigate(`/builder/${slug}`);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl gap-3 pt-3 pb-6 px-4">
        <div aria-hidden className="mx-auto w-8 h-1 rounded-full bg-black/20" />

        <SheetHeader className="p-0 pt-2">
          <SheetTitle className="text-[15px] font-semibold text-dream-navy">
            {STRINGS.shell.worldSheet.title}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {STRINGS.shell.worldSheet.description}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-2">
          {Object.values(WORLDS).map((world) => (
            <button
              key={world.slug}
              type="button"
              onClick={() => handleSelect(world.slug)}
              className="flex items-center gap-3 p-3 rounded-xl text-left transition-[filter] hover:brightness-95 active:brightness-90"
              style={{
                backgroundColor: `color-mix(in srgb, var(${world.colorVar}) 15%, white)`,
              }}
            >
              <span
                aria-hidden
                className="flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden"
                style={{
                  backgroundColor: `color-mix(in srgb, var(${world.colorVar}) 30%, white)`,
                }}
              >
                <img
                  src={getLibraryCover(world.slug)}
                  alt=""
                  className="w-full h-full object-contain"
                  draggable={false}
                  onError={(e) => {
                    // Fallback to default cover if the per-world SVG
                    // fails to load (asset missing or build-pipeline
                    // hiccup). PWA #39 swap from emoji to SVG —
                    // closes the Mai-Launch SVG-coverage gap; the
                    // graceful-degradation here keeps the bottom-
                    // sheet usable even on edge-case asset failures.
                    e.currentTarget.src = '/covers/cover-default.svg';
                  }}
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-semibold text-dream-navy truncate">
                  {world.name}
                </div>
                <div className="text-[12px] text-dream-navy/60 truncate">{world.tagline}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-dream-navy/40 shrink-0" />
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
